import {
  authResponseSchema,
  type AuthResponse,
  type FolderItemDto,
  type FolderSummary,
  type LibraryItem,
  type SearchResult,
  type SortOption,
  type Status,
  type TitleDetails,
} from '@mass-cult/shared';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

export function resolveImageUrl(url: string | null): string | null {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  if (url.startsWith('/api/')) {
    if (API_BASE.startsWith('http')) {
      const origin = API_BASE.replace(/\/api\/?$/, '');
      return `${origin}${url}`;
    }
    return url;
  }
  return url;
}

let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
  if (token) {
    localStorage.setItem('masscult_token', token);
  } else {
    localStorage.removeItem('masscult_token');
  }
}

export function getStoredToken(): string | null {
  return authToken ?? localStorage.getItem('masscult_token');
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('Content-Type', 'application/json');
  const token = getStoredToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error?.message ?? 'Ошибка запроса');
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export async function authenticate(initData: string): Promise<AuthResponse> {
  const data = await request<AuthResponse>('/auth/telegram', {
    method: 'POST',
    body: JSON.stringify({ initData }),
  });
  return authResponseSchema.parse(data);
}

export async function searchTitles(query: string, page = 1) {
  const params = new URLSearchParams({ q: query, page: String(page) });
  return request<{ page: number; totalPages: number; results: SearchResult[] }>(
    `/search?${params.toString()}`,
  );
}

export async function getTitle(mediaType: string, tmdbId: number) {
  return request<TitleDetails>(`/titles/${mediaType}/${tmdbId}`);
}

export async function getLibrary(filters: {
  status?: Status;
  type?: 'movie' | 'tv';
  sort?: SortOption;
}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.type) params.set('type', filters.type);
  if (filters.sort) params.set('sort', filters.sort);
  return request<LibraryItem[]>(`/library?${params.toString()}`);
}

export async function setStatus(
  mediaType: string,
  tmdbId: number,
  status: Status | null,
) {
  return request(`/library/${mediaType}/${tmdbId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

export async function setRating(
  mediaType: string,
  tmdbId: number,
  rating: number | null,
) {
  return request(`/library/${mediaType}/${tmdbId}/rating`, {
    method: 'PUT',
    body: JSON.stringify({ rating }),
  });
}

export async function getFolders() {
  return request<FolderSummary[]>('/folders');
}

export async function createFolder(name: string) {
  return request<FolderSummary>('/folders', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function renameFolder(id: number, name: string) {
  return request<FolderSummary>(`/folders/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
}

export async function deleteFolder(id: number) {
  return request<void>(`/folders/${id}`, { method: 'DELETE' });
}

export async function getFolderItems(id: number, sort: SortOption = 'added_desc') {
  return request<FolderItemDto[]>(`/folders/${id}?sort=${sort}`);
}

export async function addToFolder(
  folderId: number,
  mediaType: string,
  tmdbId: number,
) {
  return request<void>(`/folders/${folderId}/items/${mediaType}/${tmdbId}`, {
    method: 'PUT',
  });
}

export async function removeFromFolder(
  folderId: number,
  mediaType: string,
  tmdbId: number,
) {
  return request<void>(`/folders/${folderId}/items/${mediaType}/${tmdbId}`, {
    method: 'DELETE',
  });
}
